import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PRODUCT_TYPES } from '@core/model/datacenter';
import * as _ from 'lodash';
import { MapConfigSelector } from '@core/ngrx/selectors/map-config.selector';
import { tap, take, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as providersActions from '@core/ngrx/actions/provider.actions';
import * as marketActions from '@core/ngrx/actions/market.actions';
import { AppStateService } from '@core/service/app-state.service';
import { UsersSelector } from '@core/ngrx/selectors/users.selector';
import { usersActions } from '@core/ngrx/actions';
import { DatacenterV2 } from '@core/model/datacenter-v2';
import { Provider } from '@core/model/providers';
import { ProvidersSelector } from '@core/ngrx/selectors/providers.selector';
import { async, Observable, Subject, takeUntil } from 'rxjs';
import { MarketsSelector } from '@core/ngrx/selectors/markets.selector';
import { MapService } from '@core/service/map.service';
import { ColorUtils } from '@core/util/color-utils';
import { AvailabilityZoneHandler } from '@core/service/availability-zone.handler';

@Component({
  selector: 'app-new-data-center-popup',
  templateUrl: './new-data-center-popup.component.html',
  styleUrls: ['./new-data-center-popup.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class NewDataCenterPopupComponent implements OnInit, OnDestroy {
  latRegExp: RegExp = /^-?([0-8]?[0-9]|90)(\.[0-9]{1,10})?$/;
  lonRegExp: RegExp = /^-?([0-9]{1,2}|1[0-7][0-9]|180)(\.[0-9]{1,10})?$/;
  destroy$: Subject<boolean> = new Subject<boolean>();
  data: DatacenterV2;
  isModifying = false;
  isAddingProvider = false;
  isAddingMarket = false;
  providers: Provider[] = [];
  markets: Observable<Set<string>> = this.marketsSelector.getMarkets();
  selectedProvider = undefined;
  selectedMarket = undefined;
  productTypes = [
    {label: 'AI/ML', value: 'AI/ML'},
    {label: PRODUCT_TYPES.AVAILABILITY_ZONE, value: PRODUCT_TYPES.AVAILABILITY_ZONE},
    {label: 'Edge', value: 'Edge'},
    {label: 'Enterprise', value: 'Enterprise'},
    {label: 'Ground Station', value: 'Ground Station'},
    {label: 'Internet Exchange', value: 'Internet Exchange'},
    {label: 'Local Zone', value: 'Local Zone'},
    {label: 'Wavelength Zone', value: 'Wavelength Zone'}
  ].sort((a, b) => a.label.localeCompare(b.label));
  availabilityZoneIds = [];
  selectedAZId: string;
  isAddingNewAZ = false;
  siteTypes = [
    {label: 'Competitor', value: 'Competitor'},
    {label: 'Customer Early Stage', value: 'Customer Early Stage'},
    {label: 'Customer self-build', value: 'Customer self-build'},
    {label: 'Early Stage', value: 'Early Stage'},
    {label: 'Edge', value: 'Edge'},
    {label: 'Land Banked', value: 'Land Banked'},
    {label: 'Opportunity', value: 'Opportunity'}
  ].sort((a, b) => a.label.localeCompare(b.label));
  isAddingBuildingTenant = false;
  selectedBuildingTenant = undefined;
  mapCreationDate: Date;
  modifiedByDisplayName: string;
  selectedSites: any = [];
  selectedProductTypes: any = [];
  newBuildingTenant: string = '';
  selectedBuildingTenants: any[] = [];
  buildingTenants: any[] = [];
  isAddingDataSource = false;
  selectedDataSource = undefined;
  newDataSource: string = '';
  selectedDataSources: any = [];
  dataSources: any[] = [];
  providerNameError: string = '';
  allProviderNames: Set<string> = new Set();
  marketNameError: string = '';
  allMarketNames: Set<string> = new Set();

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private mapConfigSelector: MapConfigSelector,
    private store: Store,
    private appStateService: AppStateService,
    private usersSelector: UsersSelector,
    private providersSelector: ProvidersSelector,
    private marketsSelector: MarketsSelector,
    private mapService: MapService,
    private availabilityZoneHandler: AvailabilityZoneHandler
  ) {
  }

  ngOnInit(): void {
    this.store.dispatch(usersActions.getUsers());
    this.mapCreationDate = this.config?.data?.mapCreationDate;
    this.data = _.cloneDeep(this.config.data as DatacenterV2);
    this.providersSelector.getAllProviders().pipe(
      takeUntil(this.destroy$),
      tap(providers => {
        this.providers = providers;
        this.selectedProvider = this.providers.find(provider => provider.name === this.data.provider);
        // Load all provider names for validation
        this.allProviderNames = new Set(providers.map(p => p.name.toLowerCase().trim()));
      })
    ).subscribe();

    // Load all market names for validation
    this.markets.pipe(
      takeUntil(this.destroy$),
      tap(markets => {
        this.allMarketNames = new Set(Array.from(markets).map(m => m.toLowerCase().trim()));
      })
    ).subscribe();
    this.initializeSelectedValues();

    if (this.config.data.toUpdate) {
      this.isModifying = true;

      if (this.data.lat) {
        this.data.lat = +this.data.lat.toFixed(6);
      }
      if (this.data.lon) {
        this.data.lon = +this.data.lon.toFixed(6);
      }
    }

    this.mapConfigSelector.getExistingProviders().pipe(
      take(1),
      tap((providers: []) => {
        let sortedProviders: [] = _.cloneDeep(providers);
        sortedProviders.sort((a: string, b: string) => a.localeCompare(b));

      })
    ).subscribe();

    this.mapConfigSelector.getExistingMarkets().pipe(
      take(1),
      tap((markets: []) => {
        let sortedMarkets: [] = _.cloneDeep(markets);
        sortedMarkets.sort((a: string, b: string) => a.localeCompare(b));

      })
    ).subscribe();

    this.mapConfigSelector.getExistingBuildingTenants().pipe(
      take(1),
      tap((buildingTenants: string[]) => {
        this.buildingTenants = Array.from(new Set(
          buildingTenants
            .flat()
            .concat(this.config.data.buildingTenant?.flat())
            .filter(id => id !== null && id !== undefined && id !== '')
        )).sort((a, b) => a.localeCompare(b))
      })
    ).subscribe();

    this.mapConfigSelector.getExistingDataSources().pipe(
      take(1),
      tap((dataSources: string[]) => {
        this.dataSources = Array.from(new Set(
          dataSources
            .flat()
            .concat(this.config.data.dataSource?.flat())
            .filter(id => id !== null && id !== undefined && id !== '')
        )).sort((a, b) => a.localeCompare(b))
      })
    ).subscribe();

    this.updateModifiedByDisplayName();
    this.loadSiteTypes();
    this.loadProductTypes();
    this.loadDataSources();
    this.loadBuildingTenants();

  }

  private initializeSelectedValues(): void {
    this.selectedMarket = this.data.market;
    this.selectedBuildingTenant = this.data.buildingTenant || [];

    // Preserve existing availability zone value when modifying
    if (this.data.availabilityZoneId) {
      this.selectedAZId = this.data.availabilityZoneId;
    }

    if (this.data.provider && this.data.provider.trim() !== '') {

        this.loadAvailabilityZoneIds();
    }
  }

  private loadSiteTypes() {
    if (!Array.isArray(this.data.siteType)) {
      this.data.siteType = [];
    }

    if (this.data.siteType.length > 0) {
      this.selectedSites = this.data.siteType.map(site => ({
        label: site,
        value: site
      }));
    }
  }

  private loadProductTypes() {
    if (!Array.isArray(this.data.productType)) {
      this.data.productType = [];
    }

    if (this.data.productType.length > 0) {
      this.selectedProductTypes = this.data.productType.map(product => ({
        label: product,
        value: product
      }));
    }
  }

  private loadBuildingTenants() {
    this.buildingTenants = this.buildingTenants?.map(tenant => ({
      label: tenant,
      value: tenant
    }));

    this.selectedBuildingTenants = this.data.buildingTenant?.map(tenant => ({
      label: tenant,
      value: tenant
    }));
  }

  private loadDataSources() {
    this.dataSources = this.dataSources?.map(source => ({
      label: source,
      value: source
    }));

    this.selectedDataSources = (this.data.dataSource)?.map(source => ({
      label: source,
      value: source
    }));
  }

  updateModifiedByDisplayName() {
    this.usersSelector.getUsers().pipe(
      take(1),
      tap(users => {
      })
    ).subscribe();
  }

  onProductTypeChange() {
    this.data.productType = this.selectedProductTypes.map(product => product.value);
    if (!this.data.productType.includes(PRODUCT_TYPES.AVAILABILITY_ZONE)) {
      this.data.availabilityZoneId = null;
      this.selectedAZId = null;
      this.isAddingNewAZ = false;
      return;
    }
    this.loadAvailabilityZoneIds();
  }

  addNewTenant() {
    if (this.newBuildingTenant) {
      const newTenant = {
        label: this.newBuildingTenant.trim(),
        value: this.newBuildingTenant.trim()
      };

      if (!this.buildingTenants.some(tenant => tenant.value === newTenant.value)) {
        this.buildingTenants = [...this.buildingTenants, newTenant].sort((a, b) =>
          a.label.localeCompare(b.label));

        this.selectedBuildingTenants = [...this.selectedBuildingTenants, newTenant];

        this.data.buildingTenant = this.selectedBuildingTenants.map(tenant => tenant.value);
      }

      this.newBuildingTenant = '';
      this.isAddingBuildingTenant = false;
    }
  }

  private assignProviderImage(): void {
    this.mapConfigSelector.getDatacenters().pipe(
      take(1),
      map(datacenters => datacenters.find(dc =>
        dc.provider?.toLowerCase() === this.data.provider?.toLowerCase() &&
        dc.imageBase64
      )),
      tap(existingProvider => {
        if (existingProvider?.imageBase64) {

        }
      })
    ).subscribe();
  }

  saveNewMarker() {
    try {
      // Validate provider name if adding a new provider
      if (this.isAddingProvider) {
        this.validateProviderName();
        if (this.providerNameError !== '') {
          return;
        }
      }

      if (this.isAddingMarket) {
        this.validateMarketName();
        if (this.marketNameError !== '') {
          return;
        }
      }

      if (this.isAddingMarket && this.data.market) {
        this.store.dispatch(marketActions.addMarket({
          market: this.data.market
        }));
      }

      if (this.isAddingProvider && this.data.provider) {
        const tempProvider: Provider = {
          id: null,
          auxId: this.mapService.buildAuxId(),
          name: this.data.provider,
          base64Icon: '',
          color: ColorUtils.generateRandomColor(),
          isVisible: true
        };

        // Dispatch action to add temporary provider
        this.store.dispatch(providersActions.providersModified({
          provider: tempProvider
        }));

        this.data.providerId = tempProvider.auxId; // Use auxId for temporary providers
        this.data.providerColor = tempProvider.color;
      } else if (!this.isAddingProvider && this.selectedProvider) {
        this.data.provider = this.selectedProvider.name;
        this.data.providerId = this.selectedProvider.auxId || this.selectedProvider.id;
      }

      if (!this.isAddingMarket) {
        this.data.market = this.selectedMarket;
      }

      this.data.buildingTenant = this.selectedBuildingTenants?.map(tenant => tenant.value) || [];
      this.data.dataSource = this.selectedDataSources?.map(source => source.value) || [];

      if (this.data.productType.includes(PRODUCT_TYPES.AVAILABILITY_ZONE)) {
        this.data.availabilityZoneId = this.isAddingNewAZ ? this.data.availabilityZoneId : this.selectedAZId;
      }

      if (!this.isAddingProvider) {
        this.providersSelector.getProviders().pipe(
          take(1),
          tap((providers: Provider[]) => {
            const provider = providers.find(p => p.id === this.data.providerId);
            if (provider) {
              this.data.providerColor = provider.color;
            } else {
              this.data.providerColor = '#000000'; // Default color if provider not found
            }
            this.ref.close(_.cloneDeep(this.data));
          })
        ).subscribe();
      } else {
        this.ref.close(_.cloneDeep(this.data));
      }

    } catch (error) {
      console.error('Error saving marker:', error);
    }
  }

  updateMarkerColor() {

  }

  onSiteTypesChange() {
    this.data.siteType = this.selectedSites.map(site => site.value);
  }

  onProducTypesChange() {
    this.data.productType = this.selectedSites.map(product => product.value);
  }

  onBuildingTenantsChange() {
    this.data.buildingTenant = this.selectedBuildingTenants.map(tenant => tenant.value);
  }

  addNewDataSource() {
    if (this.newDataSource) {
      const newSource = {
        label: this.newDataSource.trim(),
        value: this.newDataSource.trim()
      };

      if (!this.dataSources.some(source => source.value === newSource.value)) {
        this.dataSources = [...this.dataSources, newSource].sort((a, b) =>
          a.label.localeCompare(b.label));

        this.selectedDataSources = [...this.selectedDataSources, newSource];

        this.data.dataSource = this.selectedDataSources.map(source => source.value);
      }

      this.newDataSource = '';
      this.isAddingDataSource = false;
    }
  }

  onAddProviderChange() {
    if (this.isAddingProvider) {
      // Clear selected provider when switching to add mode
      this.selectedProvider = undefined;
      this.data.provider = '';
      this.providerNameError = '';
      // AZ list debe resetearse hasta que haya provider válido
      this.availabilityZoneIds = [];
    } else {
      // Clear the text input when switching back to select mode
      this.data.provider = '';
      this.providerNameError = '';
    }
  }

  onAddMarketChange() {
    if (this.isAddingMarket) {
      this.selectedMarket = undefined;
      this.data.market = '';
      this.marketNameError = '';
    } else {
      this.data.market = '';
      this.marketNameError = '';
    }
  }

  validateProviderName() {
    if (!this.isAddingProvider || !this.data.provider) {
      this.providerNameError = '';
      return;
    }

    const providerName = this.data.provider.toLowerCase().trim();

    if (providerName === '') {
      this.providerNameError = 'Provider name is required';
      return;
    }

    if (this.allProviderNames.has(providerName)) {
      this.providerNameError = 'A provider with this name already exists';
      return;
    }

    this.providerNameError = '';
  }

  isProviderNameValid(): boolean {
    return !this.isAddingProvider || (this.providerNameError === '' && this.data.provider && this.data.provider.trim() !== '');
  }

  validateMarketName() {
    if (!this.isAddingMarket || !this.data.market) {
      this.marketNameError = '';
      return;
    }

    const marketName = this.data.market.toLowerCase().trim();

    if (marketName === '') {
      this.marketNameError = 'Market name is required';
      return;
    }

    if (this.allMarketNames.has(marketName)) {
      this.marketNameError = 'A market with this name already exists';
      return;
    }

    this.marketNameError = '';
  }

  isMarketNameValid(): boolean {
    return !this.isAddingMarket || (this.marketNameError === '' && this.data.market && this.data.market.trim() !== '');
  }

  onDataSourcesChange() {
    this.data.dataSource = this.selectedDataSources.map(source => source.value);
  }

  cancel() {
    this.ref.close(null);
  }

  loadAvailabilityZoneIds() {
    const currentProvider = (this.isAddingProvider || !this.selectedProvider)
      ? (this.data.provider || '')
      : this.selectedProvider.name;

    if (!currentProvider || currentProvider.trim() === '') {
      this.availabilityZoneIds = [];
      return;
    }

    // Update datacenter provider
    this.data.provider = currentProvider;
    this.availabilityZoneIds = this.availabilityZoneHandler.getAvailabilityZoneIdsByProvider(this.data.provider);

    // Preserve selected value if it exists and is valid for this provider
    const existingAZIds = this.availabilityZoneIds.map(az => az.value);
    if (this.data.availabilityZoneId && existingAZIds.includes(this.data.availabilityZoneId)) {
      this.selectedAZId = this.data.availabilityZoneId;
    } else {
      this.selectedAZId = null;
      this.data.availabilityZoneId = null;
      this.isAddingNewAZ = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  protected readonly async = async;
  protected readonly Array = Array;
}
